import {Manifest} from 'powtoon-versioning/src/currentSchema'
import {roundToInterval} from 'powtoon-commons/utils/numbers'
import {MINIMUM_STEP_SIZE} from 'powtoon-document/consts/duration'

export function getAudioDuration(
  {length = 0} = {},
  roundTrackTime = (time: number) => roundToInterval(time, MINIMUM_STEP_SIZE),
  minTimeStep = MINIMUM_STEP_SIZE
): number {
  const trackDuration = roundTrackTime(Math.max(length, minTimeStep))
  return trackDuration < length ? roundTrackTime(trackDuration + minTimeStep) : trackDuration
}

export function getAudioDurationFromManifest(
  contentItemId: string,
  manifestObject: Manifest,
  roundTrackTime = (time: number) => roundToInterval(time, MINIMUM_STEP_SIZE),
  minTimeStep = MINIMUM_STEP_SIZE
): number {
  const manifestItem = manifestObject?.sounds?.[contentItemId] || manifestObject?.assets?.[contentItemId]
  return getAudioDuration(manifestItem, roundTrackTime, minTimeStep)
}

export const createAudio = (url: string): HTMLAudioElement => {
  const audio: HTMLAudioElement = document.createElement('audio')
  audio.muted = true
  audio.src = url
  return audio
}

export interface AudioUrlMetadata {
  duration: number,
  localUrl: string,
}

export const getAudioUrlMetadata = (
  url: string,
  limitRetryCounter = 1
) => {
  return new Promise<AudioUrlMetadata>((resolve, reject) => {
    let audio: HTMLAudioElement | null = createAudio(url)

    let retryCounter = 1
    audio.play().catch(err => {
      if (retryCounter < limitRetryCounter) {
        retryCounter++
        return audio && audio.load()
      } else {
        reject(err)
        audio = null
      }
    })

    const timeupdate = async () => {
      try {
        if (audio) {
          audio.removeEventListener('timeupdate', timeupdate)
          audio.pause()
          resolve({
            localUrl: url,
            duration: audio.duration
          })
        }
      }
      catch (e) {
        reject(e)
      }
    }

    audio?.addEventListener('timeupdate', timeupdate)
  })
}

export interface WaveformPeaks {
  min: number[]
  max: number[]
}

export interface WaveformDimensions {
  width: number
  height: number
}

const extractPeaksFromRange = (
  count: number,
  getRangeForIndex: (i: number) => {start: number, end: number},
  getValueAt: (j: number) => {min: number, max: number}
): WaveformPeaks => {
  const minPeaks: number[] = []
  const maxPeaks: number[] = []

  for (let i = 0; i < count; i++) {
    const {start, end} = getRangeForIndex(i)
    
    let min = 1
    let max = -1

    for (let j = start; j < end; j++) {
      const {min: minValue, max: maxValue} = getValueAt(j)
      
      if (minValue < min) {
        min = minValue
      }
      if (maxValue > max) {
        max = maxValue
      }
    }

    minPeaks.push(min)
    maxPeaks.push(max)
  }

  return {min: minPeaks, max: maxPeaks}
}

export const extractPeaksAtMaxResolution = (
  audioBuffer: AudioBuffer,
  peaksPerSeconds: number
): WaveformPeaks => {
  const audioDuration = audioBuffer.duration
  const maxPeaksCount = Math.ceil(audioDuration * peaksPerSeconds)
  const channelData = audioBuffer.getChannelData(0)
  const samplesPerPixel = Math.floor(channelData.length / maxPeaksCount)

  return extractPeaksFromRange(
    maxPeaksCount,
    (i) => ({
      start: i * samplesPerPixel,
      end: Math.min((i + 1) * samplesPerPixel, channelData.length)
    }),
    (j) => {
      const value = channelData[j] || 0
      return {min: value, max: value}
    }
  )
}


export const generateWaveformSvg = (peaks: WaveformPeaks, color = '#ffffff'): string => {
  const numPoints = peaks.max.length
  if (numPoints === 0) {
    return ''
  }

  const topPoints = peaks.max.map((max, i) => {
    const yTop = 50 - (max * 50)
    return `${i},${yTop.toFixed(2)}`
  })

  const bottomPoints = peaks.min.slice().reverse().map((min, i) => {
    const yBottom = 50 - (min * 50)
    return `${numPoints - 1 - i},${yBottom.toFixed(2)}`
  })

  const d = `M0,50 L${topPoints.join(' L')} L${numPoints - 1},50 L${bottomPoints.join(' L')} Z`

  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${numPoints} 100" preserveAspectRatio="none"><path d="${d}" fill="${color}"/></svg>`
  return `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`
}

export const loadAudioPeaks = async (dataUrl: string, peaksPerSeconds: number) => {
  const response = await fetch(dataUrl)
  const buffer = await response.arrayBuffer()
  const audioContext = new AudioContext()
  const audioBuffer = await audioContext.decodeAudioData(buffer)
  const peaks = extractPeaksAtMaxResolution(audioBuffer, peaksPerSeconds)
  audioContext.close()
  return peaks
}
